import React, { useId, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  EMPTY_BUSINESS_NETWORK_REGISTRATION,
  normaliseBusinessNetworkSource,
  submitBusinessNetworkRegistration,
  validateBusinessNetworkRegistration,
} from "./businessNetworkRegistration";
import {
  formatBusinessNetworkEventDate,
  formatBusinessNetworkEventTime,
} from "./businessNetworkEvents";

function FieldError({ id, message }) {
  return message ? (
    <span id={id} className="text-sm text-amber-200">
      {message}
    </span>
  ) : null;
}

export default function BusinessNetworkRegistrationForm({
  event,
  source = "business-network-event-page",
}) {
  const instanceId = useId().replace(/:/g, "");
  const fieldId = (field) => `business-${instanceId}-${field}`;
  const errorId = (field) => `${fieldId(field)}-error`;
  const [values, setValues] = useState({ ...EMPTY_BUSINESS_NETWORK_REGISTRATION });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [registered, setRegistered] = useState(false);
  const errorSummaryRef = useRef(null);
  const submissionLockRef = useRef(false);
  const sourceRef = useRef(normaliseBusinessNetworkSource(source));

  const update = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setSubmitError("");
  };

  const describedBy = (field, helpId = "") =>
    [helpId, errors[field] ? errorId(field) : ""].filter(Boolean).join(" ") ||
    undefined;

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    if (submissionLockRef.current) return;

    const nextErrors = validateBusinessNetworkRegistration(values);
    setErrors(nextErrors);
    setSubmitError("");
    if (Object.keys(nextErrors).length > 0) {
      window.setTimeout(() => errorSummaryRef.current?.focus(), 0);
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);
    try {
      const result = await submitBusinessNetworkRegistration(
        values,
        sourceRef.current,
        event
      );
      if (!["accepted", "registered", "already_registered"].includes(result?.status)) {
        throw new Error("BUSINESS_NETWORK_REGISTRATION_UNCONFIRMED");
      }
      setRegistered(true);
      setValues({ ...EMPTY_BUSINESS_NETWORK_REGISTRATION });
      setErrors({});
    } catch (error) {
      console.error("Unable to submit Business Network registration", error);
      setSubmitError(
        "Your registration could not be saved. Check your connection and try again. No success has been recorded yet."
      );
      window.setTimeout(() => errorSummaryRef.current?.focus(), 0);
    } finally {
      submissionLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (registered) {
    return (
      <section
        className="rounded-3xl border border-emerald-300/25 bg-emerald-300/10 p-5 text-center sm:p-6 md:p-10"
        aria-labelledby={`${instanceId}-registration-success`}
        aria-live="polite"
      >
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-300" aria-hidden="true" />
        <h2 id={`${instanceId}-registration-success`} className="mt-4 text-3xl font-black md:text-4xl">
          You&apos;re registered for this event.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-white/80">
          If you had already registered with this email address, no duplicate was created.
        </p>
        <p className="mx-auto mt-3 max-w-2xl leading-7 text-white/75">
          We look forward to welcoming you on {formatBusinessNetworkEventDate(event)}, {formatBusinessNetworkEventTime(event)}.
        </p>
        <button
          type="button"
          onClick={() => setRegistered(false)}
          className="mt-7 inline-flex min-h-12 items-center justify-center rounded-xl border border-white/20 bg-white/10 px-5 py-3 font-bold text-white hover:bg-white/15"
        >
          Register another attendee
        </button>
      </section>
    );
  }

  const inputClass =
    "min-h-12 w-full rounded-xl border border-white/20 bg-black/35 px-4 py-3 text-white placeholder:text-white/40 focus:border-[#ff8b94] focus:outline-none focus:ring-2 focus:ring-[#ff8b94]/30";

  return (
    <section
      id="register"
      className="scroll-mt-8 rounded-3xl border border-white/10 bg-white/5 p-5 md:p-8"
      aria-labelledby={`${instanceId}-signup-title`}
    >
      <h2 id={`${instanceId}-signup-title`} className="text-2xl font-black md:text-3xl">
        Sign up to attend
      </h2>
      <p className="mt-2 text-white/70">
        Attendance is free. Register one attendee below; fields marked with an asterisk are required.
      </p>

      {Object.keys(errors).length > 0 || submitError ? (
        <div
          ref={errorSummaryRef}
          tabIndex="-1"
          role="alert"
          className="mt-5 rounded-xl border border-amber-300/30 bg-amber-300/10 p-4 text-amber-100"
        >
          {submitError || "Please check the highlighted registration details."}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} noValidate className="mt-6 grid gap-5 md:grid-cols-2">
        <label className="grid gap-2" htmlFor={fieldId("first-name")}>
          <span className="font-semibold">First name *</span>
          <input
            id={fieldId("first-name")}
            autoComplete="given-name"
            maxLength="80"
            required
            value={values.firstName}
            onChange={(inputEvent) => update("firstName", inputEvent.target.value)}
            aria-invalid={Boolean(errors.firstName)}
            aria-describedby={describedBy("firstName")}
            className={inputClass}
          />
          <FieldError id={errorId("firstName")} message={errors.firstName} />
        </label>

        <label className="grid gap-2" htmlFor={fieldId("last-name")}>
          <span className="font-semibold">Last name *</span>
          <input
            id={fieldId("last-name")}
            autoComplete="family-name"
            maxLength="80"
            required
            value={values.lastName}
            onChange={(inputEvent) => update("lastName", inputEvent.target.value)}
            aria-invalid={Boolean(errors.lastName)}
            aria-describedby={describedBy("lastName")}
            className={inputClass}
          />
          <FieldError id={errorId("lastName")} message={errors.lastName} />
        </label>

        <label className="grid gap-2" htmlFor={fieldId("email")}>
          <span className="font-semibold">Email address *</span>
          <input
            id={fieldId("email")}
            type="email"
            autoComplete="email"
            maxLength="254"
            required
            value={values.email}
            onChange={(inputEvent) => update("email", inputEvent.target.value)}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={describedBy("email")}
            className={inputClass}
          />
          <FieldError id={errorId("email")} message={errors.email} />
        </label>

        <label className="grid gap-2" htmlFor={fieldId("phone")}>
          <span className="font-semibold">
            Phone number <span className="font-normal text-white/60">(optional)</span>
          </span>
          <input
            id={fieldId("phone")}
            type="tel"
            autoComplete="tel"
            maxLength="40"
            value={values.phone}
            onChange={(inputEvent) => update("phone", inputEvent.target.value)}
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={describedBy("phone")}
            className={inputClass}
          />
          <FieldError id={errorId("phone")} message={errors.phone} />
        </label>

        <label className="grid gap-2 md:col-span-2" htmlFor={fieldId("organisation")}>
          <span className="font-semibold">Business or organisation *</span>
          <input
            id={fieldId("organisation")}
            autoComplete="organization"
            maxLength="160"
            required
            value={values.businessOrganisation}
            onChange={(inputEvent) =>
              update("businessOrganisation", inputEvent.target.value)
            }
            aria-invalid={Boolean(errors.businessOrganisation)}
            aria-describedby={describedBy("businessOrganisation")}
            className={inputClass}
          />
          <FieldError
            id={errorId("businessOrganisation")}
            message={errors.businessOrganisation}
          />
        </label>

        <label className="grid gap-2 md:col-span-2" htmlFor={fieldId("role")}>
          <span className="font-semibold">
            Role or job title <span className="font-normal text-white/60">(optional)</span>
          </span>
          <input
            id={fieldId("role")}
            autoComplete="organization-title"
            maxLength="120"
            value={values.roleOrJobTitle}
            onChange={(inputEvent) => update("roleOrJobTitle", inputEvent.target.value)}
            aria-invalid={Boolean(errors.roleOrJobTitle)}
            aria-describedby={describedBy("roleOrJobTitle")}
            className={inputClass}
          />
          <FieldError id={errorId("roleOrJobTitle")} message={errors.roleOrJobTitle} />
        </label>

        <fieldset
          className="rounded-xl border border-white/15 bg-black/20 p-4 md:col-span-2"
          aria-describedby={errors.previousBusinessNetworkAttendee ? errorId("previous") : undefined}
        >
          <legend className="px-1 font-semibold">
            Have you attended a previous Urban Unity Business Network event? *
          </legend>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:gap-5">
            {[
              [true, "Yes"],
              [false, "No"],
            ].map(([value, label]) => (
              <label
                key={label}
                className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-2"
              >
                <input
                  type="radio"
                  name={`${instanceId}-previous-attendee`}
                  value={label.toLowerCase()}
                  required
                  checked={values.previousBusinessNetworkAttendee === value}
                  onChange={() => update("previousBusinessNetworkAttendee", value)}
                  className="h-5 w-5 accent-[#E63946]"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <FieldError
            id={errorId("previous")}
            message={errors.previousBusinessNetworkAttendee}
          />
        </fieldset>

        <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
          <label htmlFor={fieldId("website")}>Leave this field empty</label>
          <input
            id={fieldId("website")}
            name="website"
            tabIndex="-1"
            autoComplete="off"
            value={values.website}
            onChange={(inputEvent) => update("website", inputEvent.target.value)}
          />
        </div>

        <label
          className="flex items-start gap-3 rounded-xl border border-white/15 bg-black/20 p-4 md:col-span-2"
          htmlFor={fieldId("event-consent")}
        >
          <input
            id={fieldId("event-consent")}
            type="checkbox"
            checked={values.eventConsent}
            onChange={(inputEvent) => update("eventConsent", inputEvent.target.checked)}
            aria-invalid={Boolean(errors.eventConsent)}
            aria-describedby={describedBy(
              "eventConsent",
              fieldId("event-consent-description")
            )}
            className="mt-1 h-5 w-5 shrink-0 accent-[#E63946]"
          />
          <span>
            <span className="font-semibold">
              I agree that Urban Unity Network may use my details to manage this event registration and contact me with essential event information. *
            </span>
            <span
              id={fieldId("event-consent-description")}
              className="mt-2 block text-sm leading-6 text-white/60"
            >
              Registration details are not published and are available only to authorised UUN administrators.
            </span>
            <FieldError id={errorId("eventConsent")} message={errors.eventConsent} />
          </span>
        </label>

        <label
          className="flex items-start gap-3 rounded-xl border border-white/15 bg-black/20 p-4 md:col-span-2"
          htmlFor={fieldId("marketing-consent")}
        >
          <input
            id={fieldId("marketing-consent")}
            type="checkbox"
            checked={values.marketingConsent}
            onChange={(inputEvent) => update("marketingConsent", inputEvent.target.checked)}
            className="mt-1 h-5 w-5 shrink-0 accent-[#E63946]"
          />
          <span>
            <span className="font-semibold">
              I would like to receive occasional Urban Unity Network news and opportunities.
            </span>
            <span className="mt-2 block text-sm leading-6 text-white/60">
              Optional. Leaving this unticked will not affect your registration.
            </span>
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          aria-disabled={submitting}
          className="inline-flex min-h-14 items-center justify-center rounded-xl bg-[#E63946] px-6 py-3 text-lg font-black text-white transition hover:bg-[#d62f3d] disabled:cursor-wait disabled:opacity-60 md:col-span-2"
        >
          {submitting ? "Saving your registration…" : "Sign up to attend"}
        </button>
      </form>
    </section>
  );
}
